import { useId, useState } from 'react';
import { trackMetaEvent } from '../lib/metaPixel';
import { getTrackingParams } from '../lib/trackingParams';
import { CONTACT_EMAIL } from '../lib/site';

interface ContactFormProps {
  compact?: boolean;
}

const SUBMIT_ERROR =
  `Je aanvraag is niet verstuurd. Controleer je verbinding en probeer opnieuw. Lukt het niet? Mail ons op ${CONTACT_EMAIL}.`;

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  interest: '',
  message: '',
  website: '' // honeypot: hidden from real users, bots tend to fill every field
};

const LABEL_BASE =
  'absolute left-0 top-4 pointer-events-none transition-all peer-focus:-top-6 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-xs uppercase tracking-[0.2em] font-medium';

const FIELD_BASE =
  'block w-full bg-transparent border-b py-4 focus:outline-none focus:border-primary focus:shadow-[0_1px_0_0_var(--color-primary)] transition-all peer text-lg font-light';

export default function ContactForm({ compact = false }: ContactFormProps) {
  const uid = useId();
  const id = (key: string) => `${uid}-${key}`;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<{ email?: string; phone?: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as 'email' | 'phone']) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check: real users never see or fill this field, bots usually do.
    // Pretend success so bots don't learn to skip it.
    if (formData.website) {
      setSubmitStatus('success');
      setFormData(EMPTY_FORM);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const errors: { email?: string; phone?: string } = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Vul je e-mailadres in, zodat we je kunnen bereiken.';
    } else if (!emailRegex.test(formData.email.trim())) {
      errors.email = 'Dit e-mailadres lijkt niet te kloppen. Controleer het (bv. naam@voorbeeld.be).';
    }

    // Strip whitespaces, dashes, dots, and parentheses
    const cleanedPhone = formData.phone.replace(/[\s\-\.\(\)]/g, '');
    const phoneRegex = /^(?:\+32|0032|0)(?:[1-9]\d{7}|4\d{8})$/;

    if (!formData.phone.trim()) {
      errors.phone = 'Vul je telefoonnummer in, zodat we je kunnen bellen.';
    } else if (!phoneRegex.test(cleanedPhone)) {
      errors.phone = 'Dit nummer lijkt niet te kloppen. Gebruik een Belgisch nummer, bv. 0470 12 34 56 of +32 470 12 34 56.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    setFormErrors({});

    const tracking = getTrackingParams();

    try {
      setErrorMessage(null);
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase
        .from('LEO - LEADS')
        .insert([
          {
            'Voornaam': formData.firstName,
            'Naam': formData.lastName,
            'Email': formData.email,
            'Phone': formData.phone,
            'Type Klant': formData.interest,
            'Message': formData.message,
            ...tracking,
          }
        ]);

      if (error) throw error;

      setSubmitStatus('success');

      // Track Lead event with Meta Conversions API and Pixel (under GDPR consent)
      trackMetaEvent({
        eventName: 'Lead',
        customData: {
          content_name: 'Registratie Formulier',
          content_category: formData.interest || 'Lead',
        },
        userData: {
          email: formData.email,
          phone: formData.phone,
          firstName: formData.firstName,
          lastName: formData.lastName,
        }
      });

      setFormData(EMPTY_FORM);
    } catch (error: any) {
      console.error('Full Supabase Error:', error);
      setSubmitStatus('error');
      setErrorMessage(SUBMIT_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div role="status" className="py-8">
        <p className="font-serif text-3xl md:text-4xl tracking-wide text-text-main mb-4">Bedankt!</p>
        <p className="text-secondary leading-relaxed">
          Je aanvraag is ontvangen. We nemen snel contact met je op.
        </p>
      </div>
    );
  }

  const spacing = compact ? 'space-y-8' : 'space-y-12';
  const gridGap = compact ? 'gap-6' : 'gap-12';
  const borderFor = (hasError: boolean) => (hasError ? 'border-red-600' : 'border-secondary/70');
  const labelColor = (hasError: boolean) => (hasError ? 'text-red-600' : 'text-secondary');

  return (
    <form className={spacing} onSubmit={handleSubmit}>
      <p className="text-sm text-secondary">Velden met <span aria-hidden="true">*</span><span className="sr-only">een sterretje</span> zijn verplicht.</p>

      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor={id('website')}>Website</label>
        <input
          type="text"
          id={id('website')}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={handleInputChange}
        />
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridGap}`}>
        <div className="relative group">
          <input
            type="text"
            id={id('firstName')}
            name="firstName"
            autoComplete="given-name"
            required
            placeholder=" "
            value={formData.firstName}
            onChange={handleInputChange}
            className={`${FIELD_BASE} ${borderFor(false)}`}
          />
          <label htmlFor={id('firstName')} className={`${LABEL_BASE} ${labelColor(false)}`}>
            Voornaam <span aria-hidden="true">*</span>
          </label>
        </div>
        <div className="relative group">
          <input
            type="text"
            id={id('lastName')}
            name="lastName"
            autoComplete="family-name"
            required
            placeholder=" "
            value={formData.lastName}
            onChange={handleInputChange}
            className={`${FIELD_BASE} ${borderFor(false)}`}
          />
          <label htmlFor={id('lastName')} className={`${LABEL_BASE} ${labelColor(false)}`}>
            Naam <span aria-hidden="true">*</span>
          </label>
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridGap}`}>
        <div className="relative group">
          <input
            type="email"
            id={id('email')}
            name="email"
            autoComplete="email"
            required
            placeholder=" "
            value={formData.email}
            onChange={handleInputChange}
            aria-invalid={!!formErrors.email}
            aria-describedby={formErrors.email ? id('email-error') : undefined}
            className={`${FIELD_BASE} ${borderFor(!!formErrors.email)}`}
          />
          <label htmlFor={id('email')} className={`${LABEL_BASE} ${labelColor(!!formErrors.email)}`}>
            E-mailadres <span aria-hidden="true">*</span>
          </label>
          {formErrors.email && (
            <p id={id('email-error')} role="alert" className="text-red-600 text-xs mt-2">
              {formErrors.email}
            </p>
          )}
        </div>
        <div className="relative group">
          <input
            type="tel"
            id={id('phone')}
            name="phone"
            autoComplete="tel"
            required
            placeholder=" "
            value={formData.phone}
            onChange={handleInputChange}
            aria-invalid={!!formErrors.phone}
            aria-describedby={formErrors.phone ? id('phone-error') : undefined}
            className={`${FIELD_BASE} ${borderFor(!!formErrors.phone)}`}
          />
          <label htmlFor={id('phone')} className={`${LABEL_BASE} ${labelColor(!!formErrors.phone)}`}>
            Telefoonnummer <span aria-hidden="true">*</span>
          </label>
          {formErrors.phone && (
            <p id={id('phone-error')} role="alert" className="text-red-600 text-xs mt-2">
              {formErrors.phone}
            </p>
          )}
        </div>
      </div>

      <div className="relative group">
        <select
          id={id('interest')}
          name="interest"
          required
          value={formData.interest}
          onChange={handleInputChange}
          className="block w-full bg-transparent border-b border-secondary/70 py-4 focus:outline-none focus:border-primary focus:shadow-[0_1px_0_0_var(--color-primary)] transition-all text-lg font-light appearance-none rounded-none cursor-pointer"
        >
          <option value="" disabled className="text-secondary">Selecteer je interesse</option>
          <option value="kopen">Ik wil een woning kopen</option>
          <option value="investeren">Ik wil investeren</option>
        </select>
        <label htmlFor={id('interest')} className="absolute left-0 -top-6 text-xs text-primary uppercase tracking-[0.2em] font-medium">
          Interesse <span aria-hidden="true">*</span>
        </label>
        <div className="absolute right-0 top-6 pointer-events-none text-secondary group-hover:text-primary transition-colors" aria-hidden="true">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        </div>
      </div>

      {!compact && (
        <div className="relative group">
          <textarea
            id={id('message')}
            name="message"
            placeholder=" "
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
            className={`${FIELD_BASE} ${borderFor(false)} resize-none min-h-[100px]`}
          />
          <label htmlFor={id('message')} className={`${LABEL_BASE} ${labelColor(false)}`}>
            Bericht (optioneel)
          </label>
        </div>
      )}

      <div className={compact ? 'pt-4' : 'pt-8'}>
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-primary text-white px-12 py-6 text-sm tracking-[0.3em] uppercase hover:bg-text-main transition-all w-full isometric-hover font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Versturen...' : 'Verstuur aanvraag'}
        </button>

        <p className="mt-4 text-xs text-secondary leading-relaxed">
          We gebruiken je gegevens enkel om contact met je op te nemen over Project LEO. Lees onze{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
            privacyverklaring
          </a>
          .
        </p>

        {submitStatus === 'error' && (
          <p role="alert" className="mt-4 text-red-600 text-sm font-medium">
            {errorMessage}
          </p>
        )}
      </div>
    </form>
  );
}
